import { getCommunicationKey, decrypt } from '../utils/encryption';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export async function fetchBrandConfig() {
  const url = `${API_BASE}/admin/brand-config`;
  try {
    const headers = {};
    const commKey = getCommunicationKey();
    if (commKey) {
      headers['X-Communication-Key'] = commKey;
    }
    const res = await fetch(url, { headers });
    if (!res.ok) {
      console.warn('[BrandConfig] Fetch failed — HTTP', res.status, '—', url);
      return null;
    }
    const json = await res.json();
    let data = json?.data ?? null;
    if (json?.encrypted === true && typeof json.data === 'string') {
      const decrypted = await decrypt(json.data);
      data = decrypted?.data ?? decrypted ?? null;
    }
    if (!data || typeof data !== 'object') {
      console.warn('[BrandConfig] Empty response from', url);
      return null;
    }
    return data;
  } catch (err) {
    console.error('[BrandConfig] Network error —', url, err);
    return null;
  }
}

// Visible text of a rich-text HTML string (used to tell an empty editor, e.g. "<p></p>", from real content)
export function htmlToPlainText(html) {
  return String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

export function buildEngagementConfig(brandData, defaults = {}) {
  const eng = brandData?.engagement ?? {};
  const ec  = eng.campaign ?? {};
  const ect = eng.content  ?? {};
  const es  = eng.social   ?? {};
  const el  = eng.legal    ?? {};
  const dc  = defaults.campaign ?? {};
  const dct = defaults.content  ?? {};
  const ds  = defaults.social   ?? {};
  const dl  = defaults.legal    ?? {};

  return {
    campaign: {
      name:        ec.name        || dc.name        || '',
      description: ec.description || dc.description || '',
      country:     ec.country     || dc.country     || '',
      year:        new Date().getFullYear(),
    },
    content: {
      welcomeTitle:    ect.welcomeTitle    || dct.welcomeTitle    || '',
      welcomeSubtitle: ect.welcomeSubtitle || dct.welcomeSubtitle || '',
      welcomeCta:      ect.welcomeCta      || dct.welcomeCta      || '',
      welcomeImageUrl: ect.welcomeImageUrl || dct.welcomeImageUrl || '',
    },
    theme: {
      primaryColor:   eng.theme?.primaryColor   || '',
      secondaryColor: eng.theme?.secondaryColor || '',
    },
    social: {
      facebook:  es.facebook  || ds.facebook  || '',
      instagram: es.instagram || ds.instagram || '',
      youtube:   es.youtube   || ds.youtube   || '',
      twitter:   es.twitter   || ds.twitter   || '',
    },
    legal: {
      termsUrl:         el.termsUrl         || dl.termsUrl         || dl.terms        || '',
      cookiePolicyUrl:  el.cookiePolicyUrl  || dl.cookiePolicyUrl  || dl.cookiePolicy || '',
      privacyPolicyUrl: el.privacyPolicyUrl || dl.privacyPolicyUrl || '',
    },
    carousel_slides: eng.carousel_slides ?? defaults.carousel_slides ?? [],
    faq_items: eng.faq_items ?? defaults.faq_items ?? [],
    terms_content: eng.terms_content ?? defaults.terms_content ?? [],
    about_content: eng.about_content ?? defaults.about_content ?? '',
    footer_copyright: eng.footer_copyright || defaults.footer_copyright || 'All rights reserved.',
    everybody_wins: {
      enabled:       !!(eng.everybody_wins?.enabled),
      mode:          eng.everybody_wins?.mode || 'tiered',
      button_frequency: ['daily', 'always'].includes(eng.everybody_wins?.button_frequency) ? eng.everybody_wins.button_frequency : 'once',
      button_daily_limit: Math.max(1, Math.floor(Number(eng.everybody_wins?.button_daily_limit) || 1)),
      team_mappings: eng.everybody_wins?.team_mappings ?? [],
      tier1_players: eng.everybody_wins?.tier1_players ?? [],
      tier2_players: eng.everybody_wins?.tier2_players ?? [],
      tier3_players: eng.everybody_wins?.tier3_players ?? [],
      tier4_players: eng.everybody_wins?.tier4_players ?? [],
    },
    features:  eng.features ?? defaults.features ?? {},
    google_tag_id: eng.google_tag_id || '',
    fb_pixel_id:   eng.fb_pixel_id   || '',
    clarity_id:    eng.clarity_id    || '',
    active_template_key: eng.active_template_key || defaults.active_template_key || '',
    logoUrl:    brandData?.logo_url    || '',
    appName:    brandData?.app_name    || '',
    faviconUrl: brandData?.favicon_url || '',
    // Site-wide notice: scrolling bar at the top ('marquee') or cookie-style pop-up ('cookie')
    // text is rich-text HTML from the dashboard editor
    noticeBanner: {
      enabled:  !!(eng.notice_banner?.enabled && htmlToPlainText(eng.notice_banner?.text)),
      text:     htmlToPlainText(eng.notice_banner?.text) ? eng.notice_banner.text : '',
      style:    eng.notice_banner?.style === 'cookie' ? 'cookie' : 'marquee',
      imageUrl: eng.notice_banner?.image_url?.trim() || '',
      ctaText:  eng.notice_banner?.cta_text?.trim() || '',
      declineText: eng.notice_banner?.decline_text?.trim() || '',
      ctaAction: ['everybody_wins', 'generate_code'].includes(eng.notice_banner?.cta_action) ? eng.notice_banner.cta_action : 'dismiss',
      codeInfoText: eng.notice_banner?.code_info_text?.trim() || '',
      frequency: eng.notice_banner?.frequency === 'every_visit' ? 'every_visit' : 'daily',
      blockPage: !!eng.notice_banner?.block_page,
    },
    // Campaign status/pause info — drives the landing page pause popup
    platformStatus:  eng.platform_status  || 'OPEN',
    pauseMessage:    eng.pause_message    || '',
    pausePillText:   eng.pause_pill_text  || '',
    pauseHeading:    eng.pause_heading    || '',
    pauseResumeAt:   eng.pause_resume_at  || '',
  };
}

